"use client";

import { c as _c } from "react/compiler-runtime";
import { ArrowLeft, ArrowRight, Sparkles } from "lucide-react";
import Markdown from "markdown-to-jsx";
import Link from "next/link";
import { ComparisonTable } from "@/components/ComparisonTable";
import { Footer } from "@/components/Footer";
import { Header } from "@/components/Header";
import { I18nText } from "@/components/I18nText";
import { CodeCTATracker } from "@/components/LandingTracker";
import { Button } from "@/components/ui/button";
import { getLocalizedComparison } from "@/lib/comparisons";
import { getMarkdownOptions } from "@/lib/utils/markdown";
import { useI18n } from "@llmgateway/i18n/react";
import type { LocalizedComparisonGroup, SerializedComparison } from "@/lib/comparisons";
interface CompareDetailClientProps {
  comparison: LocalizedComparisonGroup<SerializedComparison>;
}
function formatDate(date: string, locale: string): string {
  return new Date(date).toLocaleDateString(locale, {
    year: "numeric",
    month: "long",
    day: "numeric"
  });
}
export function CompareDetailClient(t0) {
  const $ = _c(44);
  const {
    comparison
  } = t0;
  const {
    locale
  } = useI18n();
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  if ($[0] !== comparison || $[1] !== locale) {
    const entry = getLocalizedComparison(comparison, locale);
    t6 = "min-h-screen bg-background";
    if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <Header />;
      $[9] = t7;
    } else {
      t7 = $[9];
    }
    let t8;
    if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-muted/60 via-transparent to-transparent" />;
      $[10] = t8;
    } else {
      t8 = $[10];
    }
    let t9;
    if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = <Link href="/compare" className="mb-6 inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"><ArrowLeft className="h-3.5 w-3.5" /><I18nText translationKey="compare.allComparisons" /></Link>;
      $[11] = t9;
    } else {
      t9 = $[11];
    }
    let t10;
    if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-border/60 bg-muted/50 px-4 py-1.5 text-sm text-muted-foreground"><Sparkles className="h-3.5 w-3.5" /><I18nText translationKey="compare.comparison" /></div>;
      $[12] = t10;
    } else {
      t10 = $[12];
    }
    let t11;
    if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
      t11 = <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">DevPass</div>;
      $[13] = t11;
    } else {
      t11 = $[13];
    }
    const t12 = <div className="rounded-xl border bg-card px-4 py-3">{t11}<div className="mt-0.5 font-mono text-lg font-semibold tabular-nums">{entry.devpassPrice}</div></div>;
    const t13 = <div className="rounded-xl border bg-card px-4 py-3"><div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{entry.competitor}</div><div className="mt-0.5 font-mono text-lg font-semibold tabular-nums">{entry.competitorPrice}</div></div>;
    let t14;
    if ($[14] !== t12 || $[15] !== t13) {
      t14 = <div className="mt-8 flex flex-wrap gap-3">{t12}{t13}</div>;
      $[14] = t12;
      $[15] = t13;
      $[16] = t14;
    } else {
      t14 = $[16];
    }
    let t15;
    if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
      t15 = <CodeCTATracker cta="get_started" location="compare_hero"><Button size="lg" className="gap-2" asChild={true}><Link href="/signup?plan=pro"><I18nText translationKey="compare.getDevPass" /><ArrowRight className="h-4 w-4" /></Link></Button></CodeCTATracker>;
      $[17] = t15;
    } else {
      t15 = $[17];
    }
    let t16;
    if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
      t16 = <div className="mt-8 flex flex-col gap-3 sm:flex-row">{t15}<Button size="lg" variant="ghost" asChild={true}><Link href="/pricing"><I18nText translationKey="compare.seeAllPlans" /></Link></Button></div>;
      $[18] = t16;
    } else {
      t16 = $[18];
    }
    const t17 = <div className="container relative mx-auto max-w-4xl px-4 pt-16 pb-12 sm:pt-20">{t9}{t10}<h1 className="text-4xl font-bold tracking-tight sm:text-5xl">{entry.title}</h1><p className="mt-5 max-w-2xl text-lg leading-relaxed text-muted-foreground">{entry.tagline}</p>{t14}{t16}</div>;
    if ($[19] !== t17) {
      t1 = <section className="relative overflow-hidden border-b">{t8}{t17}</section>;
      $[19] = t17;
      $[20] = t1;
    } else {
      t1 = $[20];
    }
    let t18;
    if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
      t18 = <p className="mb-2 text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground"><I18nText translationKey="compare.shortVersion" /></p>;
      $[21] = t18;
    } else {
      t18 = $[21];
    }
    const t19 = <div className="rounded-2xl border bg-muted/30 p-6 sm:p-8">{t18}<p className="text-lg leading-relaxed text-foreground">{entry.verdict}</p></div>;
    if ($[22] !== t19) {
      t2 = <section className="px-4 py-12"><div className="container mx-auto max-w-4xl">{t19}</div></section>;
      $[22] = t19;
      $[23] = t2;
    } else {
      t2 = $[23];
    }
    const t20 = <div className="container mx-auto max-w-4xl"><h2 className="mb-2 text-2xl font-bold tracking-tight sm:text-3xl"><I18nText translationKey="compare.atGlance" values={{
          competitor: entry.competitor
        }} /></h2><p className="mb-6 text-sm text-muted-foreground"><I18nText translationKey="compare.pricingAsOf" values={{
          tagline: entry.competitorTagline,
          date: formatDate(entry.date, locale)
        }} /></p><ComparisonTable competitor={entry.competitor} features={entry.features} /></div>;
    if ($[24] !== t20) {
      t3 = <section className="px-4 pb-4">{t20}</section>;
      $[24] = t20;
      $[25] = t3;
    } else {
      t3 = $[25];
    }
    let t21;
    if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
      t21 = getMarkdownOptions();
      $[26] = t21;
    } else {
      t21 = $[26];
    }
    const t22 = <article><Markdown options={t21}>{entry.content}</Markdown></article>;
    if ($[27] !== t22) {
      t4 = <section className="px-4 py-12"><div className="container mx-auto max-w-3xl">{t22}</div></section>;
      $[27] = t22;
      $[28] = t4;
    } else {
      t4 = $[28];
    }
    t5 = entry.faqs.length > 0 && <section className="border-t bg-muted/20 px-4 py-16"><div className="container mx-auto max-w-3xl"><h2 className="mb-8 text-2xl font-bold tracking-tight sm:text-3xl"><I18nText translationKey="compare.faq" /></h2><div className="divide-y divide-border/60">{entry.faqs.map(_temp)}</div></div></section>;
    $[0] = comparison;
    $[1] = locale;
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
    $[5] = t4;
    $[6] = t5;
    $[7] = t6;
    $[8] = t7;
  } else {
    t1 = $[2];
    t2 = $[3];
    t3 = $[4];
    t4 = $[5];
    t5 = $[6];
    t6 = $[7];
    t7 = $[8];
  }
  let t8;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <h2 className="mb-3 text-3xl font-bold tracking-tight"><I18nText translationKey="compare.oneKey" /></h2>;
    $[29] = t8;
  } else {
    t8 = $[29];
  }
  let t9;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <p className="mb-8 text-muted-foreground"><I18nText translationKey="compare.bottomCta" /></p>;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  let t10;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <CodeCTATracker cta="get_started" location="compare_bottom_cta"><Button size="lg" className="gap-2 px-8" asChild={true}><Link href="/signup?plan=pro"><I18nText translationKey="compare.getDevPass" /><ArrowRight className="h-4 w-4" /></Link></Button></CodeCTATracker>;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  let t11;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <section className="border-t px-4 py-20"><div className="container mx-auto max-w-2xl text-center">{t8}{t9}<div className="flex flex-col items-center gap-3 sm:flex-row sm:justify-center">{t10}<Button size="lg" variant="ghost" asChild={true}><Link href="/compare"><I18nText translationKey="compare.compareOtherPlans" /></Link></Button></div></div></section>;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  let t12;
  if ($[33] !== t1 || $[34] !== t2 || $[35] !== t3 || $[36] !== t4 || $[37] !== t5) {
    t12 = <main>{t1}{t2}{t3}{t4}{t5}{t11}</main>;
    $[33] = t1;
    $[34] = t2;
    $[35] = t3;
    $[36] = t4;
    $[37] = t5;
    $[38] = t12;
  } else {
    t12 = $[38];
  }
  let t13;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <Footer />;
    $[39] = t13;
  } else {
    t13 = $[39];
  }
  let t14;
  if ($[40] !== t12 || $[41] !== t6 || $[42] !== t7) {
    t14 = <div className={t6}>{t7}{t12}{t13}</div>;
    $[40] = t12;
    $[41] = t6;
    $[42] = t7;
    $[43] = t14;
  } else {
    t14 = $[43];
  }
  return t14;
}
function _temp(item) {
  return <div key={item.question} className="py-5"><h3 className="text-lg font-medium text-foreground">{item.question}</h3><p className="mt-2 leading-7 text-muted-foreground">{item.answer}</p></div>;
}